import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Column, ColumnInstance, Row, useTable } from 'react-table';
import { PaginatedListContentProps } from '../../../types';
import {
  initialQueryState,
  KTCard,
  KTCardBody,
  KTIcon,
  useDebounce,
} from '../../helpers';
import ActionButton from '../components/ActionButton.tsx';
import HTooltip from '../components/HTooltip.tsx';
import ListLoading from '../components/ListLoading.tsx';
import CustomHeaderColumn from './CustomHeaderColumn.tsx';
import { CustomRow } from './CustomRow.tsx';
import ListFilter from './ListFilter.tsx';
import ListPagination from './ListPagination.tsx';
import { ListViewProvider, useListView } from './ListViewProvider.tsx';
import {getEnvironment} from '../../../app/environment';
import {selectApplication} from '../../../app/store/application';
import {useAppSelector} from '../../../app/store/hooks';
import {
  QueryRequestProvider,
  useQueryRequest,
} from './QueryRequestProvider.tsx';
import {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponseLoading,
} from './QueryResponseProvider.tsx';

const PaginatedListContent = forwardRef(function PaginatedListContent<
  T extends object,
>(
  {
    columns,
    title,
    showSearch = false,
    filters,
    listActions = [],
    queryList,
    actions,
    empty,
    id,
  }: PaginatedListContentProps<T>,
  ref: React.ForwardedRef<HTMLAnchorElement>
) {
  // ref.current = this;
  const { t } = useTranslation();
  const isLoading = useQueryResponseLoading();
  const data = useQueryResponseData<T>();
  const { state, updateState } = useQueryRequest();
  const hasCriteria = Boolean(state.search?.trim() || Object.values(state.filter ?? {}).some(value => value !== '' && value !== null && value !== undefined));
  const resetCriteria = () => {
    setSearchTerm('');
    clearSelected();
    updateState({...initialQueryState, search:'', filter:{}});
  };
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { getTableProps, getTableBodyProps, headers, rows, prepareRow } =
    useTable({
      columns,
      data,
    });
  const debouncedSearchTerm = useDebounce(searchTerm, 150);
  const { selected, clearSelected } = useListView();
  const { query, refetch, isError } = useQueryResponse();

  useEffect(
    () => {
      if (debouncedSearchTerm !== undefined && searchTerm !== undefined) {
        updateState({ search: debouncedSearchTerm, ...initialQueryState });
      }
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
    // More details about useDebounce: https://usehooks.com/useDebounce/
  );

  const searchInput = (
    <div className='d-flex align-items-center position-relative my-1 me-2'>
      <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
      <input
        type='text'
        data-kt-user-table-filter='search'
        className='form-control form-control-solid w-250px ps-14'
        placeholder={t('Search')}
        aria-label={t('Search')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        data-cy={'search-input'}
      />
    </div>
  );

  return (
    <KTCard className='mb-5 mb-xl-10'>
      <div className='card-header cursor-pointer'>
        {title && (
          <div className='card-title m-0'>
            {typeof title == 'string' ? (
              <h3 className='fw-bold m-0'>{title}</h3>
            ) : (
              title
            )}
          </div>
        )}
        {!title && showSearch && searchInput}
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end align-items-center me-2'>
            {title && showSearch && searchInput}
            {(listActions.length == 0 || selected?.length == 0) && filters && (
              <ListFilter filters={filters} />
            )}
            {selected?.length == 0 &&
              actions?.map((a) => (
                <button
                  onClick={a.onClick}
                  type='button'
                  data-cy={a['data-cy']}
                  className={a.className ?? 'btn btn-light-primary me-3'}
                >
                  {a.icon && <KTIcon iconName={a.icon} className='fs-2' />}
                  {a.title}
                </button>
              ))}
            {selected?.length > 0 && (
              <div className='d-flex justify-content-end align-items-center'>
                <div className='fw-bolder me-5'>
                  <span className='me-2'>{selected.length}</span>{' '}
                  {t('Selected', { count: selected.length })}
                </div>
                {listActions.map((action) => (
                  <ActionButton
                    title={action.title}
                    action={action.action}
                    className={action.className}
                    onFinish={() => {
                      queryClient.invalidateQueries([`${queryList}-${query}`]);
                      clearSelected();
                      // updateState({search: debouncedSearchTerm, filter, ...initialQueryState});
                    }}
                    data={{ ids: selected, ...(action.data || {}) }}
                    confirmation={action.confirmation}
                    data-cy={action['data-cy']}
                  />
                ))}
              </div>
            )}
          </div>
          <HTooltip title={t('Reload data')}>
            <a
              ref={ref}
              href='#'
              aria-label={t('Reload data')}
              className='btn btn-icon btn-active-light-primary'
              onClick={event => {
                event.preventDefault();
                void refetch();
              }}
            >
              {!isLoading && <KTIcon iconName='loading' className='fs-1' />}
              {isLoading && (
                <span
                  className='indicator-progress'
                  style={{ display: 'block' }}
                >
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </a>
          </HTooltip>
        </div>
        {/*<button className="btn btn-sm btn-primary align-self-center" onClick={() => setModal(true)}>{t('Update Settings')}</button>*/}
      </div>
      <KTCardBody>
        {isError && <div role='alert' className='alert alert-warning d-flex align-items-center justify-content-between gap-3'>
          <span>{t('workspace_data_error')}</span>
          <button type='button' className='btn btn-sm btn-light-primary flex-shrink-0' disabled={isLoading} onClick={() => void refetch()}>{t('Retry')}</button>
        </div>}
        <div className='table-responsive mb-2'>
          <table
            id={id}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
            {...getTableProps()}
          >
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                {headers.map((column: ColumnInstance<T>) => (
                  <CustomHeaderColumn key={column.id} column={column} />
                ))}
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
              {rows.length > 0 &&
                rows.map((row: Row<T>, i) => {
                  prepareRow(row);
                  return <CustomRow row={row} key={`row-${i}-${row.id}`} />;
                })}
            </tbody>
          </table>
        </div>
        {rows.length == 0 && !isLoading && !isError && (
          <div role='status'>
            {hasCriteria ? <div className='text-center py-6'>
              <p className='text-gray-600 mb-4'>{t('No matching records found')}</p>
              <button type='button' className='btn btn-sm btn-light-primary' onClick={resetCriteria}>{t('Clear search and filters')}</button>
            </div> : empty ? empty : (
              <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                {t('No matching records found')}
              </div>
            )}
          </div>
        )}
        <ListPagination />
        {isLoading && <ListLoading />}
      </KTCardBody>
    </KTCard>
  );
});

function PaginatedListInner<T extends object>(
  {
    url,
    columns,
    title,
    showSearch,
    filters,
    listActions,
    queryList,
    actions,
    empty,
    id,
  }: PaginatedListContentProps<T> & { url: string },
  ref: React.ForwardedRef<HTMLAnchorElement>
) {
  const application = useAppSelector(selectApplication);
  return (
    <QueryRequestProvider key={`${getEnvironment()}:${application?.key ?? ''}:${url}`}>
      <QueryResponseProvider url={url} queryList={queryList}>
        <ListViewProvider>
          <PaginatedListContent
            columns={columns as unknown as readonly Column<object>[]}
            title={title}
            showSearch={showSearch}
            filters={filters}
            listActions={listActions}
            queryList={queryList}
            actions={actions}
            empty={empty}
            ref={ref}
            id={id}
          />
        </ListViewProvider>
      </QueryResponseProvider>
    </QueryRequestProvider>
  );
}

const PaginatedList = forwardRef(
  PaginatedListInner
) as <T extends object>(props: PaginatedListContentProps<T> & { url: string; ref?: React.ForwardedRef<HTMLAnchorElement> }) => React.ReactElement;

export default PaginatedList;
